import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { T, useLang } from "@/i18n";
import { LangSwitcher } from "@/components/LangSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Incarico() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [lang, setLang] = useLang();
  const t = T[lang].mandate;
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [nome, setNome] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setLoadError(true);
      return;
    }
    axios
      .get(`${API}/mandate/${sessionId}`)
      .then(({ data }) => setInfo(data))
      .catch(() => setLoadError(true));
  }, [sessionId]);

  const sign = async (e) => {
    e.preventDefault();
    if (!nome.trim() || !accepted) {
      toast.error(t.err_required);
      return;
    }
    setSigning(true);
    try {
      await axios.post(`${API}/mandate`, { session_id: sessionId, nome, accepted });
      setInfo({ ...info, signed: true, signed_by: nome });
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.err_submit);
    } finally {
      setSigning(false);
    }
  };

  if (loadError) {
    return (
      <div className="bg-[#200910] text-[#faf6ef] min-h-screen flex items-center justify-center px-6">
        <div className="max-w-xl w-full border border-[#4d1a2a] bg-[#33101c] p-10" data-testid="mandate-error">
          <h1 className="font-display uppercase text-2xl">{t.err_t}</h1>
          <p className="text-sm text-[#b4939c] mt-3">{t.err_d}</p>
          <Link to="/" data-testid="m-back-home" className="inline-block mt-6 px-6 py-3 border border-[#d3b870] text-[#d3b870] font-mono-lbl uppercase tracking-[0.14em] text-xs hover:bg-[#d3b870] hover:text-[#200910] transition-colors">{t.err_btn}</Link>
        </div>
      </div>
    );
  }

  if (info && (!info.paid || info.signed)) {
    return (
      <div className="bg-[#200910] text-[#faf6ef] min-h-screen flex items-center justify-center px-6">
        <div className="max-w-xl w-full border border-[#4d1a2a] bg-[#33101c] p-10" data-testid="mandate-done">
          <span className="font-mono-lbl text-xs uppercase tracking-[0.18em] text-[#d3b870]">{t.kicker}{info.package_name || ""}</span>
          <h1 className="font-display uppercase text-3xl mt-3">{info.paid ? t.done_t : t.notpaid_t}</h1>
          <p className="text-sm text-[#b4939c] mt-4">{info.paid ? t.done_d : t.notpaid_d}</p>
          {info.signed && (
            <Link to={`/questionario?session_id=${sessionId}`} data-testid="m-goto-questionnaire"
              className="inline-block mt-8 px-6 py-3 bg-[#d3b870] text-[#200910] font-display uppercase tracking-wider text-sm hover:bg-[#ecd9ab] transition-colors">
              {t.done_btn}
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#200910] text-[#faf6ef] min-h-screen font-sans py-16 px-6">
      <div className="max-w-2xl mx-auto" data-testid="mandate-page">
        <div className="flex justify-end mb-6"><LangSwitcher lang={lang} setLang={setLang} /></div>
        <span className="font-mono-lbl text-xs uppercase tracking-[0.18em] text-[#d3b870]">{t.kicker}{info?.package_name || ""}</span>
        <h1 className="font-display uppercase text-[clamp(28px,4vw,44px)] leading-[0.98] mt-3" data-testid="mandate-title">{t.title}</h1>
        <p className="text-sm text-[#b4939c] mt-4 leading-relaxed">{t.intro}</p>

        <div className="mt-10 border border-[#4d1a2a] bg-[#33101c] p-8 space-y-5" data-testid="mandate-letter">
          {t.letter.map((p, i) => (
            <p key={i} className="text-sm text-[#faf6ef] leading-relaxed">
              {p.replace("{package}", info?.package_name || "")}
            </p>
          ))}
          <p className="text-[11px] font-mono-lbl uppercase tracking-[0.14em] text-[#b4939c] pt-4 border-t border-[#4d1a2a]">
            Trust Master SRL — Via Guglielmo Oberdan 23, 45100 Rovigo (RO) — P.IVA 01683810293
          </p>
        </div>

        <form onSubmit={sign} className="mt-8 space-y-6" data-testid="mandate-form">
          <div>
            <label className="block font-mono-lbl text-xs uppercase tracking-[0.14em] text-[#d3b870] mb-2" htmlFor="m-nome">{t.nome_label}</label>
            <input id="m-nome" data-testid="m-nome" required value={nome} onChange={(e) => setNome(e.target.value)}
              className="w-full bg-[#33101c] border border-[#4d1a2a] px-4 py-3 text-sm text-[#faf6ef] focus:border-[#d3b870] outline-none" />
          </div>
          <label className="flex items-start gap-3 text-xs text-[#b4939c] cursor-pointer" data-testid="m-accept-label">
            <input type="checkbox" data-testid="m-accept" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 accent-[#d3b870]" />
            <span>{t.accept}</span>
          </label>
          <button type="submit" data-testid="m-sign" disabled={signing}
            className="w-full py-4 bg-[#d3b870] hover:bg-[#ecd9ab] text-[#200910] font-display uppercase tracking-wider text-sm transition-colors font-bold disabled:opacity-60">
            {signing ? t.signing : t.sign_btn}
          </button>
        </form>
      </div>
    </div>
  );
}
